import { eventsApi } from './eventsApi';
import { staysApi } from './staysApi';
import { isAllLocationsSelection, normalizeCityKey } from '../utils/cityUtils';

const STAY_SEARCH_PAGE = 100;

/**
 * Load stays for the header / modal text search. Uses the stays search API with
 * pagination so we are not limited to the first 20 from GET /stays.
 */
async function fetchStaysForTextSearch() {
  const all = [];
  let offset = 0;
  for (;;) {
    const res = await staysApi.searchStays({ limit: STAY_SEARCH_PAGE, offset });
    const page = res.stays || [];
    all.push(...page);
    if (!res.pagination?.hasMore || page.length === 0) break;
    offset += STAY_SEARCH_PAGE;
  }
  return all;
}

async function fetchEventsForTextSearch() {
  const all = [];
  let offset = 0;
  for (;;) {
    const res = await eventsApi.getAllEvents({ limit: STAY_SEARCH_PAGE, offset });
    const page = res.events || [];
    all.push(...page);
    if (!res.pagination?.hasMore || page.length === 0) break;
    offset += STAY_SEARCH_PAGE;
  }
  return all;
}

function normalizeForTextMatch(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function staySearchHaystack(stay) {
  return [
    stay.name,
    stay.title,
    stay.description,
    stay.city,
    stay.state,
    stay.location,
    stay.address,
    stay.stayId,
    stay.id
  ]
    .filter(Boolean)
    .map(normalizeForTextMatch)
    .join(' ');
}

function stayMatchesTextQuery(stay, searchText) {
  const q = normalizeForTextMatch(searchText);
  if (!q) return false;
  const hay = staySearchHaystack(stay);
  if (hay.includes(q)) return true;
  const qCompact = q.replace(/\s+/g, '');
  const hayCompact = hay.replace(/\s+/g, '');
  return qCompact.length > 0 && hayCompact.includes(qCompact);
}

/**
 * Search API Service
 * Handles unified search across events and stays
 */
export const searchApi = {
  /**
   * Search across both events and stays
   * @param {string} query - Search query
   * @param {Object} options - Search options
   * @param {string} options.type - Search type ('all', 'events', 'stays')
   * @param {number} [options.limit] - Optional cap per category (omit for all matches)
   * @returns {Promise<Object>} Combined search results
   */
  searchAll: async (query, options = {}) => {
    try {
      const { type = 'all', limit, selectedCity } = options;
      const scopeKey =
        selectedCity != null &&
        String(selectedCity).trim() !== '' &&
        !isAllLocationsSelection(selectedCity)
          ? normalizeCityKey(selectedCity)
          : null;
      const results = {
        events: [],
        stays: [],
        total: 0
      };

      // Search events if type is 'all' or 'events'
      if (type === 'all' || type === 'events') {
        try {
          const allEvents = await fetchEventsForTextSearch();
          
          // Filter events by query (search in name, description, category)
          let filteredEvents = allEvents.filter(event => {
            const searchText = query.toLowerCase();
            return (
              (event.name && event.name.toLowerCase().includes(searchText)) ||
              (event.title && event.title.toLowerCase().includes(searchText)) ||
              (event.description && event.description.toLowerCase().includes(searchText)) ||
              (event.category && event.category.toLowerCase().includes(searchText))
            );
          });
          
          // Additional filtering to remove past events
          const todayDateOnly = new Date();
          todayDateOnly.setHours(0, 0, 0, 0);
          
          filteredEvents = filteredEvents.filter(event => {
            try {
              if (!event.endDate) {
                return true; // Include events without endDate
              }
              
              const eventEndDate = new Date(event.endDate);
              eventEndDate.setHours(0, 0, 0, 0);
              
              if (isNaN(eventEndDate.getTime())) {
                return true; // Include events with invalid dates
              }
              
              return eventEndDate >= todayDateOnly;
            } catch (error) {
              console.log(`❌ Error filtering search event "${event.title}":`, error.message);
              return true; // Include events with errors
            }
          });
          
          console.log('Filtered events:', filteredEvents.map(e => ({ id: e.id, eventId: e.eventId, name: e.name || e.title })));
          
          results.events =
            limit != null ? filteredEvents.slice(0, limit) : filteredEvents;
        } catch (error) {
          console.error('Error searching events:', error);
          results.events = [];
        }
      }

      // Search stays if type is 'all' or 'stays'
      if (type === 'all' || type === 'stays') {
        try {
          const q = (query || '').trim();
          let allStays = await fetchStaysForTextSearch();

          // Typed search (e.g. "kalani", stay ID) searches all cities; navbar city only scopes empty browse
          const searchAcrossAllCities = q.length >= 2;
          if (scopeKey && !searchAcrossAllCities) {
            allStays = allStays.filter((stay) => normalizeCityKey(stay?.city) === scopeKey);
          }

          const filteredStays = allStays.filter((stay) => stayMatchesTextQuery(stay, q));

          const sorted = scopeKey
            ? [...filteredStays].sort((a, b) => {
                const aIn = normalizeCityKey(a?.city) === scopeKey ? 0 : 1;
                const bIn = normalizeCityKey(b?.city) === scopeKey ? 0 : 1;
                return aIn - bIn;
              })
            : filteredStays;

          results.stays = limit != null ? sorted.slice(0, limit) : sorted;
        } catch (error) {
          console.error('Error searching stays:', error);
          results.stays = [];
        }
      }

      results.total = results.events.length + results.stays.length;
      return results;
    } catch (error) {
      console.error('Search API error:', error);
      throw error;
    }
  },

  /**
   * Get recent searches from localStorage
   * @returns {Array} Array of recent search terms
   */
  getRecentSearches: () => {
    try {
      const recent = localStorage.getItem('recentSearches');
      return recent ? JSON.parse(recent) : [];
    } catch (error) {
      console.error('Error getting recent searches:', error);
      return [];
    }
  },

  /**
   * Save search query to recent searches
   * @param {string} query - Search query to save
   */
  saveRecentSearch: (query) => {
    try {
      if (!query || query.trim() === '') return;
      
      const trimmedQuery = query.trim();
      const recent = searchApi.getRecentSearches();
      
      // Remove if already exists
      const filtered = recent.filter(item => item !== trimmedQuery);
      
      // Add to beginning
      filtered.unshift(trimmedQuery);
      
      // Keep only last 10 searches
      const limited = filtered.slice(0, 10);
      
      localStorage.setItem('recentSearches', JSON.stringify(limited));
    } catch (error) {
      console.error('Error saving recent search:', error);
    }
  },

  /**
   * Clear recent searches
   */
  clearRecentSearches: () => {
    try {
      localStorage.removeItem('recentSearches');
    } catch (error) {
      console.error('Error clearing recent searches:', error);
    }
  },

  /**
   * Get search suggestions based on query
   * @param {string} query - Partial search query
   * @returns {Promise<Array>} Array of suggestions
   */
  getSuggestions: async (query, searchOptions = {}) => {
    try {
      if (!query || query.length < 2) return [];
      
      const results = await searchApi.searchAll(query, { limit: 5, ...searchOptions });
      const suggestions = [];
      
      // Add event suggestions
      results.events.forEach(event => {
        suggestions.push({
          id: `event-${event.id}`,
          text: event.name || event.title,
          type: 'event',
          data: event
        });
      });
      
      // Add stay suggestions
      results.stays.forEach(stay => {
        suggestions.push({
          id: `stay-${stay.id}`,
          text: stay.name || stay.title,
          type: 'stay',
          data: stay
        });
      });
      
      return suggestions.slice(0, 8); // Limit to 8 suggestions
    } catch (error) {
      console.error('Error getting suggestions:', error);
      return [];
    }
  }
};

export default searchApi;
